import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { I18NextService } from 'angular-i18next';

export interface SubscriptionTier {
    id: string;
    name: string;
    displayName: string;
    badge?: string;
    features: TierFeature[];
    isCurrentPackage?: boolean;
}

export interface TierFeature {
    name: string;
    included: boolean;
}

@Component({
    selector: 'ham-subscription-tiers',
    templateUrl: './ham-subscription-tiers.component.html',
    styleUrls: ['./ham-subscription-tiers.component.scss'],
})
export class HamSubscriptionTiersComponent implements OnInit, OnChanges {
    showDowngradeDialog = false;
    showUpgradeDialog = false;
    pendingTierId: string | null = null;

    tiers: SubscriptionTier[] = [
        {
            id: 'light',
            name: 'aumico_components_subscription_tiers_tier_light',
            displayName: 'aumico_components_subscription_tiers_tier_light',
            isCurrentPackage: false,
            features: [
                { name: 'aumico_components_subscription_tiers_feature_report_generation', included: true },
            ],
        },
        {
            id: 'standard',
            name: 'aumico_components_subscription_tiers_tier_standard',
            displayName: 'aumico_components_subscription_tiers_tier_standard',
            isCurrentPackage: false,
            badge: 'aumico_components_subscription_tiers_most_popular',
            features: [
                { name: 'aumico_components_subscription_tiers_feature_report_generation', included: true },
                { name: 'aumico_components_subscription_tiers_feature_mandate_task_management', included: true },
                { name: 'aumico_components_subscription_tiers_feature_financial_closing_workflow', included: true },
                { name: 'aumico_components_subscription_tiers_feature_interim_statements', included: true },
            ],
        },
        {
            id: 'pro',
            name: 'aumico_components_subscription_tiers_tier_pro',
            displayName: 'aumico_components_subscription_tiers_tier_pro',
            isCurrentPackage: false,
            features: [
                { name: 'aumico_components_subscription_tiers_feature_report_generation', included: true },
                { name: 'aumico_components_subscription_tiers_feature_mandate_task_management', included: true },
                { name: 'aumico_components_subscription_tiers_feature_financial_closing_workflow', included: true },
                { name: 'aumico_components_subscription_tiers_feature_individual_reporting_templates', included: true },
                { name: 'aumico_components_subscription_tiers_feature_employee_training', included: true },
                { name: 'aumico_components_subscription_tiers_feature_dedicated_customer_success_manager', included: true },
                { name: 'aumico_components_subscription_tiers_feature_interim_statements', included: true },
            ],
        },
    ];

    constructor(private readonly localizedTexts: I18NextService) { }
    @Input() currentTierId: string | null = null;

    @Output() switchTier = new EventEmitter<string>();
    @Output() contactSales = new EventEmitter<void>();

    ngOnInit(): void {
        this.updateTierStates();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['currentTierId']) {
            this.updateTierStates();
        }
    }

    private updateTierStates(): void {
        if (this.currentTierId) {
            this.tiers = this.tiers.map((tier) => ({
                ...tier,
                isCurrentPackage: tier.id === this.currentTierId,
            }));
        }
    }

    private getTierIndex(tierId: string): number {
        return this.tiers.findIndex(tier => tier.id === tierId);
    }

    private getTierLevel(tierId: string): number {
        const index = this.getTierIndex(tierId);
        // Return -1 if tier not found, otherwise return the index (0-based level)
        return index === -1 ? -1 : index;
    }

    private isUpgrade(fromTierId: string, toTierId: string): boolean {
        const fromLevel = this.getTierLevel(fromTierId);
        const toLevel = this.getTierLevel(toTierId);

        // Invalid tiers should not trigger upgrade/downgrade logic
        if (fromLevel === -1 || toLevel === -1) {
            return false;
        }

        // Higher index = higher tier, so upgrade means moving to higher index
        return toLevel > fromLevel;
    }

    get downgradeWarningFeatures(): string {
        if (this.pendingTierId && this.currentTierId) {
            if (this.currentTierId === 'standard') {
                return this.localizedTexts.t('aumico_ui_billing_downgrade_warning_features_standard').toString();
            } if (this.currentTierId === 'pro') {
                return this.localizedTexts.t('aumico_ui_billing_downgrade_warning_features_pro').toString();
            }
        }
        return '';
    }

    private isDowngrade(fromTierId: string, toTierId: string): boolean {
        const fromLevel = this.getTierLevel(fromTierId);
        const toLevel = this.getTierLevel(toTierId);

        // Invalid tiers should not trigger upgrade/downgrade logic
        if (fromLevel === -1 || toLevel === -1) {
            return false;
        }

        // Lower index = lower tier, so downgrade means moving to lower index
        return toLevel < fromLevel;
    }

    onSwitchTier(tierId: string): void {
        if (!this.currentTierId) {
            // If no current tier is set, switch immediately
            this.performTierSwitch(tierId);
            return;
        }

        if (this.currentTierId === tierId) {
            // Same tier, no action needed
            return;
        }

        // Validate that both tiers exist
        const currentTierExists = this.getTierLevel(this.currentTierId) !== -1;
        const targetTierExists = this.getTierLevel(tierId) !== -1;

        if (!currentTierExists || !targetTierExists) {
            console.warn('Invalid tier IDs:', { currentTierId: this.currentTierId, targetTierId: tierId });
            return;
        }

        this.pendingTierId = tierId;

        if (this.isUpgrade(this.currentTierId, tierId)) {
            console.log('Upgrade detected:', this.currentTierId, '->', tierId);
            this.showUpgradeDialog = true;
        } else if (this.isDowngrade(this.currentTierId, tierId)) {
            console.log('Downgrade detected:', this.currentTierId, '->', tierId);
            this.showDowngradeDialog = true;
        } else {
            // This shouldn't happen given our validation above, but just in case
            console.warn('Unexpected tier switch scenario:', this.currentTierId, '->', tierId);
            this.performTierSwitch(tierId);
        }
    }

    private performTierSwitch(tierId: string): void {
        // Don't update internal state - let the parent component handle this
        // The parent will update currentTierId, which will trigger ngOnChanges and updateTierStates
        this.switchTier.emit(tierId);
    }

    onConfirmDowngrade() {
        if (this.pendingTierId) {
            this.performTierSwitch(this.pendingTierId);
            this.pendingTierId = null;
        }
        this.showDowngradeDialog = false;
    }

    onConfirmUpgrade() {
        if (this.pendingTierId) {
            this.performTierSwitch(this.pendingTierId);
            this.pendingTierId = null;
        }
        this.showUpgradeDialog = false;
    }

    onCancelDowngrade() {
        this.pendingTierId = null;
        this.showDowngradeDialog = false;
    }

    onCancelUpgrade() {
        this.pendingTierId = null;
        this.showUpgradeDialog = false;
    }

    onContactSales(): void {
        this.contactSales.emit();
    }
}